import React, { useState } from "react";
import { stageStatusColor, TankLevel, PumpBadge } from "./stpHelpers";

const STAGE_COLOR = { NORMAL: "#10b981", WARNING: "#f59e0b", ALARM: "#ef4444" };

// Flow positions in a two-row SVG layout
const SVG_STAGES = [
  { id: "SRC", name: "Inlet",              x: 2,  y: 10, w: 12, h: 18, isSource: true },
  { id: "S1",  name: "Screen\nChamber",    x: 18, y: 10, w: 14, h: 18 },
  { id: "S2",  name: "Equalization\nTank", x: 36, y: 10, w: 14, h: 18 },
  { id: "S3",  name: "Aeration\nTank",     x: 54, y: 10, w: 14, h: 18 },
  { id: "S4",  name: "Secondary\nClarifier",x: 72, y: 10, w: 14, h: 18 },
  { id: "S5",  name: "Sand\nFilter",       x: 54, y: 52, w: 14, h: 18 },
  { id: "S6",  name: "Chlorination\nTank", x: 36, y: 52, w: 14, h: 18 },
  { id: "S7",  name: "Treated Water\nTank",x: 18, y: 52, w: 14, h: 18 },
  { id: "DST", name: "Reuse /\nDischarge", x: 2,  y: 52, w: 12, h: 18, isDest: true },
  { id: "S8",  name: "Sludge\nHolding",    x: 72, y: 52, w: 14, h: 18, isSludge: true },
];

// Arrow paths connecting stages
const ARROWS = [
  "M14,19 L18,19",           // Inlet → Screen
  "M32,19 L36,19",           // Screen → EQ
  "M50,19 L54,19",           // EQ → Aeration
  "M68,19 L72,19",           // Aeration → Clarifier
  "M79,28 L79,52",           // Clarifier → Sand Filter (down)
  "M54,61 L50,61",           // Sand Filter → Chlorination
  "M36,61 L32,61",           // Chlorination → Treated Water
  "M18,61 L14,61",           // Treated Water → Reuse
  "M72,28 C70,40 72,45 72,52", // Clarifier → Sludge (left curve)
];

const StpProcessFlowPanel = ({ d }) => {
  const [selected, setSelected] = useState(null);
  const sel = selected ? d.processStages.find(s => s.id === selected) : null;
  const selSt = sel ? stageStatusColor(sel.status) : null;

  const getStageData = (id) => d.processStages.find(s => s.id === id);

  return (
    <div className="stp-flow-layout">
      {/* Legend */}
      <div className="stp-flow-legend">
        {[["🟢 Normal","#10b981"],["🟡 Warning","#f59e0b"],["🔴 Alarm","#ef4444"]].map(([lbl, col]) => (
          <span key={lbl} className="stp-badge" style={{ background: col + "18", color: col }}>{lbl}</span>
        ))}
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#6b7280" }}>Click any tank for details</span>
      </div>

      <div className="stp-flow-body">
        {/* ── SVG Process Diagram ── */}
        <div className="stp-flow-svg-wrap">
          <svg viewBox="0 0 100 75" className="stp-flow-svg" preserveAspectRatio="xMidYMid meet">
            {/* Background */}
            <rect x="0" y="0" width="100" height="75" fill="#f8fafc" rx="2" />

            {/* Flow direction label */}
            <text x="50" y="6" textAnchor="middle" fontSize="2.2" fontWeight="700" fill="#6b7280">STP PROCESS FLOW DIAGRAM</text>

            {/* Row divider */}
            <line x1="2" y1="40" x2="98" y2="40" stroke="#e5e7eb" strokeWidth="0.3" strokeDasharray="1,1" />

            {/* Arrows */}
            {ARROWS.map((d, i) => (
              <path key={i} d={d} fill="none" stroke="#94a3b8" strokeWidth="0.5"
                markerEnd="url(#arrow)" />
            ))}

            {/* Arrow marker */}
            <defs>
              <marker id="arrow" markerWidth="4" markerHeight="4" refX="2" refY="2" orient="auto">
                <path d="M0,0 L0,4 L4,2 z" fill="#94a3b8" />
              </marker>
            </defs>

            {/* Stages */}
            {SVG_STAGES.map(stage => {
              const data = getStageData(stage.id);
              const col = stage.isSource ? "#3b82f6" : stage.isDest ? "#10b981" : stage.isSludge ? "#f59e0b" :
                (data ? STAGE_COLOR[data.status] || "#10b981" : "#94a3b8");
              const isSelected = selected === stage.id;

              return (
                <g key={stage.id} onClick={() => data && setSelected(stage.id === selected ? null : stage.id)}
                  style={{ cursor: data ? "pointer" : "default" }}>
                  <rect x={stage.x} y={stage.y} width={stage.w} height={stage.h}
                    fill={col + "18"} stroke={col} strokeWidth={isSelected ? 0.8 : 0.5} rx="1" />
                  {/* Level bar */}
                  {data && data.level != null && (
                    <rect x={stage.x + 0.5} y={stage.y + stage.h - (stage.h - 1) * (data.level / 100) - 0.5}
                      width={stage.w - 1} height={(stage.h - 1) * (data.level / 100)}
                      fill={col + "30"} rx="0.5" />
                  )}
                  {/* Title lines */}
                  {stage.name.split("\n").map((line, li) => (
                    <text key={li} x={stage.x + stage.w / 2} y={stage.y + 4 + li * 3}
                      textAnchor="middle" fontSize="1.8" fontWeight="600" fill={col}>{line}</text>
                  ))}
                  {/* Level text */}
                  {data && data.level != null && (
                    <text x={stage.x + stage.w / 2} y={stage.y + stage.h - 2}
                      textAnchor="middle" fontSize="1.5" fill="#374151">{data.level}%</text>
                  )}
                  {/* Status dot */}
                  {data && (
                    <circle cx={stage.x + stage.w - 2} cy={stage.y + 2} r="1.2" fill={col} />
                  )}
                  {/* Selected highlight */}
                  {isSelected && (
                    <rect x={stage.x - 0.5} y={stage.y - 0.5} width={stage.w + 1} height={stage.h + 1}
                      fill="none" stroke="white" strokeWidth="1" rx="1.5" />
                  )}
                  {/* Source / Dest labels */}
                  {stage.isSource && <text x={stage.x + stage.w / 2} y={stage.y + stage.h - 5} textAnchor="middle" fontSize="1.4" fill="#1d4ed8">RAW</text>}
                  {stage.isDest   && <text x={stage.x + stage.w / 2} y={stage.y + stage.h - 5} textAnchor="middle" fontSize="1.4" fill="#065f46">OUT</text>}
                </g>
              );
            })}

            {/* Row labels */}
            <text x="1" y="8"  fontSize="1.6" fill="#6b7280" fontWeight="600">Treatment →</text>
            <text x="1" y="50" fontSize="1.6" fill="#6b7280" fontWeight="600">← Polishing</text>
          </svg>
        </div>

        {/* ── Detail panel ── */}
        <div className="stp-flow-detail">
          {sel ? (
            <div className="stp-fd-card">
              <div className="stp-fd-header" style={{ borderColor: selSt.color }}>
                <div>
                  <div className="stp-fd-title">{sel.name}</div>
                  <div className="stp-fd-id">{sel.id}</div>
                </div>
                <span className="stp-badge" style={{ background: selSt.bg, color: selSt.color, fontSize: 13 }}>{selSt.label}</span>
              </div>

              {/* Key readings */}
              <div className="stp-fd-readings">
                {[
                  { label: "Tank Level",   value: sel.level != null ? `${sel.level}%` : "—" },
                  { label: "Inlet Flow",   value: sel.flowIn != null ? `${sel.flowIn} m³/hr` : "—" },
                  { label: "Outlet Flow",  value: sel.flowOut != null ? `${sel.flowOut} m³/hr` : "—" },
                  { label: "pH",           value: sel.ph != null ? sel.ph : "—" },
                  { label: "TSS",          value: sel.tss != null ? `${sel.tss} mg/L` : "—" },
                  { label: "DO",           value: sel.do != null ? `${sel.do} mg/L` : "—", warn: sel.do != null && sel.do < 2 },
                  { label: "MLSS",         value: sel.mlss != null ? `${sel.mlss} mg/L` : "—" },
                  { label: "Turbidity",    value: sel.turbidity != null ? `${sel.turbidity} NTU` : "—" },
                  { label: "Chlorine",     value: sel.chlorine != null ? `${sel.chlorine} mg/L` : "—" },
                  { label: "Blower RPM",   value: sel.blowerRpm != null ? sel.blowerRpm : "—" },
                ].filter(r => r.value !== "—").map(r => (
                  <div key={r.label} className="stp-fd-reading" style={r.warn ? { background: "#fee2e2" } : {}}>
                    <div className="stp-fd-r-val" style={{ color: r.warn ? "#ef4444" : "#1f2937" }}>{r.value}</div>
                    <div className="stp-fd-r-lbl">{r.label}</div>
                    {r.warn && <div style={{ fontSize: 9, color: "#ef4444" }}>⚠ Below min</div>}
                  </div>
                ))}
              </div>

              {/* Pump & Blower status */}
              <div className="stp-fd-equip">
                {sel.pumpStatus  && <div className="stp-fd-equip-row">Pump: <PumpBadge status={sel.pumpStatus} /></div>}
                {sel.blowerStatus && <div className="stp-fd-equip-row">Blower: <PumpBadge status={sel.blowerStatus} /></div>}
              </div>
            </div>
          ) : (
            <div className="stp-flow-no-sel">
              <div style={{ fontSize: 32 }}>🏭</div>
              <div className="stp-fns-title">Click a process tank</div>
              <div className="stp-fns-sub">Select any stage to view live telemetry</div>
              <div className="stp-fns-stages">
                {d.processStages.map(s => {
                  const st = stageStatusColor(s.status);
                  return (
                    <div key={s.id} className="stp-fns-chip" style={{ borderColor: st.color, color: st.color }}>
                      {s.id}: {st.label}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StpProcessFlowPanel;
